import React from 'react';
import { MapPin, Navigation } from 'lucide-react';
import C from '../../constants/colors';
import { EMERGENCY_META } from '../../constants/meta';

function formatTimeAgo(isoString) {
  if (!isoString) return 'Unknown';
  const diff = Date.now() - new Date(isoString).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60)  return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function AlertCard({ alert, onAcknowledge }) {
  const meta  = EMERGENCY_META[alert.emergencyType] || EMERGENCY_META.OTHER;
  const EIcon = meta.Icon;
  const isActive = !alert.acknowledged && !alert.isAmbient;
  const canAcknowledge = !alert.acknowledged;

  return (
    <div
      className={`alert-card${isActive ? ' alert-pulse' : ''}`}
      style={{
        background:   C.surfaceElevated,
        border:       `1px solid ${isActive ? meta.color + '44' : C.border}`,
        borderLeft:   `3px solid ${meta.color}`,
        borderRadius: '10px',
        padding:      '12px 14px',
        marginBottom: '8px',
        animation:    isActive ? 'alertPulse 2s infinite' : undefined,
      }}
    >
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '8px' }}>
        <div
          style={{
            width:        '32px',
            height:       '32px',
            borderRadius: '8px',
            background:   meta.color + '22',
            display:      'flex',
            alignItems:   'center',
            justifyContent: 'center',
            flexShrink:   0,
          }}
        >
          <EIcon size={16} color={meta.color} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary }}>
              {alert.employeeName}
            </span>
            <span
              style={{
                fontSize:     '10px',
                fontWeight:   500,
                color:        meta.color,
                background:   meta.color + '1a',
                padding:      '1px 7px',
                borderRadius: '10px',
              }}
            >
              {meta.label}
            </span>
            {alert.isAmbient && (
              <span
                style={{
                  fontSize:     '10px',
                  color:        C.textMuted,
                  background:   'rgba(255,255,255,0.06)',
                  padding:      '1px 7px',
                  borderRadius: '10px',
                }}
              >
                Ambient
              </span>
            )}
            {alert.acknowledged && (
              <span
                style={{
                  fontSize:     '10px',
                  color:        C.green,
                  background:   C.green + '1a',
                  padding:      '1px 7px',
                  borderRadius: '10px',
                }}
              >
                Resolved
              </span>
            )}
          </div>
          <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>
            {alert.employeeIdCode} &middot; {alert.department}
          </div>
        </div>
        <div style={{ fontSize: '11px', color: C.textMuted, whiteSpace: 'nowrap', flexShrink: 0 }}>
          {formatTimeAgo(alert.timestamp)}
        </div>
      </div>

      {/* Location row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: canAcknowledge ? '10px' : '0' }}>
        <MapPin size={11} color={C.textMuted} />
        <span style={{ fontSize: '11px', color: C.textSecondary }}>
          {alert.siteName}
          {alert.zoneName ? ` › ${alert.zoneName}` : ''}
        </span>
        <span style={{ fontSize: '11px', color: C.textMuted, marginLeft: 'auto' }}>
          {alert.companyName}
        </span>
      </div>

      {/* GPS */}
      {alert.gps && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '4px', marginBottom: canAcknowledge ? '8px' : '0' }}>
          <Navigation size={11} color={C.blue} />
          <span style={{ fontSize: '11px', color: C.blue, fontFamily: 'JetBrains Mono, monospace' }}>
            {alert.gps.lat.toFixed(4)}, {alert.gps.lng.toFixed(4)}
          </span>
        </div>
      )}

      {/* Acknowledged by */}
      {alert.acknowledged && alert.acknowledgedBy && (
        <div style={{ marginTop: '6px', fontSize: '11px', color: C.textMuted, fontStyle: 'italic' }}>
          Resolved by {alert.acknowledgedBy}
          {alert.notes ? ` — ${alert.notes}` : ''}
        </div>
      )}

      {/* Action button */}
      {canAcknowledge && onAcknowledge && (
        <button
          className="btn"
          onClick={() => onAcknowledge(alert.id)}
          style={{
            marginTop:    '10px',
            width:        '100%',
            padding:      '7px',
            background:   meta.color + '22',
            border:       `1px solid ${meta.color}44`,
            borderRadius: '7px',
            color:        meta.color,
            fontSize:     '12px',
            fontWeight:   600,
            cursor:       'pointer',
            fontFamily:   'inherit',
          }}
        >
          Acknowledge Alert
        </button>
      )}
    </div>
  );
}
